/**
 * pages/InstructorDashboard.jsx
 * Session is instructor-scoped — no course_id needed to open.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useQueueSSE } from "../hooks/useQueueSSE";
import { useChime } from "../hooks/useChime";

import QueueCardStack from "../components/instructor/QueueCardStack";
import SidePanel from "../components/instructor/SidePanel";
import TopBar from "../components/instructor/TopBar";
import ManageTab from "../components/instructor/ManageTab";
import AdminTab from "../components/instructor/AdminTab";
import AnalyticsTab from "../components/instructor/AnalyticsTab";
import PredictionsTab from "../components/instructor/PredictionsTab";
import ManageSemesterTab from "../components/instructor/ManageSemesterTab";
import AnnouncementBanner from "../components/shared/AnnouncementBanner";
import NedryModal from "../components/shared/NedryModal";
import styles from "./InstructorDashboard.module.css";

const BRIDGE_URL = "http://localhost:37731/handoff";

async function fireHandoff(data) {
  try {
    await fetch(BRIDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        student_email: data.student_email || null,
        student_first: data.student_first || null,
        student_last: data.student_last || null,
        course_code: data.course_code || null,
        competency_name: data.competency_name || null,
        // The Interview Manager's own competency id, round-tripped through
        // our import. Lets the bridge match on an identifier rather than a
        // display name, so renaming a competency cannot break the handoff.
        competency_source_id: data.competency_source_id ?? null,
      }),
    });
  } catch (err) {
    // Expected when the Interview Manager is not running. The same catch
    // also swallows mixed-content blocks and CORS rejections, which are
    // indistinguishable from it here — so log rather than discard.
    console.debug("Interview Manager handoff failed:", err);
  }
}

export default function InstructorDashboard() {
  const { user, token, apiFetch } = useAuth();

  const [activeTab, setActiveTab] = useState("queue");
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);

  // Chimes when someone new joins the queue — the event an instructor is most
  // likely to miss while helping the person in front of them. Off by default.
  const chime = useChime("gradiance_instructor_chime", { defaultOn: false });
  // Compared against the incoming count so a reorder, completion or removal
  // doesn't ring. Only growth means somebody arrived.
  const waitingCountRef = useRef(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [policy, setPolicy] = useState("FCFS");
  const [announcement, setAnnouncement] = useState("");
  const [courses, setCourses] = useState([]);
  const [classPriorityCourseIds, setClassPriorityCourseIds] = useState([]);
  const [blockNonPriority, setBlockNonPriority] = useState(false);
  const [testStudents, setTestStudents] = useState([]);
  const [returnPrompt, setReturnPrompt] = useState(null); // {entryId, name, spots}
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [pooling, setPooling] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [nedry, setNedry] = useState(null);
  const [onlineStudentIds, setOnlineStudentIds] = useState([]);

  useQueueSSE(session?.session_id, token, {
    onQueueUpdated: useCallback(
      (data) => {
        void data.timestamp;
        const incoming = (data.entries || []).filter(
          (e) => e.status === "WAITING",
        ).length;
        if (incoming > waitingCountRef.current) chime.play();
        waitingCountRef.current = incoming;
        setEntries(data.entries || []);
        setIsPaused(data.is_paused);
        setIsLocked(data.is_locked || false);
        setPolicy(data.policy);
        setAnnouncement(data.announcement || "");
        setOnlineStudentIds(data.online_student_ids || []);
      },
      [chime],
    ),
    onStudentCalled: useCallback((data) => {
      fireHandoff(data);
    }, []),
    onPaused: useCallback((data) => setIsPaused(data.is_paused), []),
    onSessionClosed: useCallback(() => {
      setSession(null);
      setPooling(null);
      // The announcement lived on the now-closed session; clear the banner too,
      // otherwise it lingers with no session to dismiss it against.
      setAnnouncement("");
      localStorage.removeItem("gradiance_instructor_session");
    }, []),
    onAnnouncement: useCallback((data) => setAnnouncement(data.message), []),
    _onPoll: useCallback(() => {
      if (session?.session_id) {
        apiFetch(`/api/queue/sessions/${session.session_id}/instructor`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data) {
              setEntries(data.entries || []);
              setIsPaused(data.is_paused);
              setIsLocked(data.swap_locked);
              setClassPriorityCourseIds(data.class_priority_course_ids || []);
              setBlockNonPriority(data.block_non_priority || false);
              setPooling(data.pooling || null);
            }
          })
          .catch(() => {});
      }
    }, [session?.session_id, apiFetch]),
  });

  const loadCourses = async () => {
    try {
      const res = await apiFetch("/api/courses/mine");
      if (res.ok) setCourses(await res.json());
    } catch {
      /* ignore */
    }
  };

  const loadActiveSession = async () => {
    try {
      // Check localStorage
      const saved = localStorage.getItem("gradiance_instructor_session");
      if (saved) {
        const s = JSON.parse(saved);
        const res = await apiFetch(
          `/api/queue/sessions/${s.session_id}/instructor`,
        );
        if (res.ok) {
          const data = await res.json();
          setSession(s);
          setEntries(data.entries || []);
          setIsPaused(data.is_paused);
          setIsLocked(data.swap_locked);
          setPolicy(data.policy);
          setAnnouncement(data.announcement || "");
          setClassPriorityCourseIds(data.class_priority_course_ids || []);
          setBlockNonPriority(data.block_non_priority || false);
          setPooling(data.pooling || null);
          return;
        }
        localStorage.removeItem("gradiance_instructor_session");
      }

      // Check if instructor has an active session right now
      const res = await apiFetch("/api/queue/active-session/instructor");
      if (res.ok) {
        const s = await res.json();
        const detailRes = await apiFetch(
          `/api/queue/sessions/${s.session_id}/instructor`,
        );
        if (detailRes.ok) {
          const data = await detailRes.json();
          const sessionObj = {
            session_id: s.session_id,
            instructor_id: s.instructor_id,
            policy: s.policy,
            display_label: data.display_label || s.display_label || null,
          };
          setSession(sessionObj);
          setEntries(data.entries || []);
          setIsPaused(data.is_paused);
          setIsLocked(data.swap_locked);
          setPolicy(data.policy);
          setAnnouncement(data.announcement || "");
          setClassPriorityCourseIds(data.class_priority_course_ids || []);
          setBlockNonPriority(data.block_non_priority || false);
          setPooling(data.pooling || null);
          localStorage.setItem(
            "gradiance_instructor_session",
            JSON.stringify(sessionObj),
          );
        }
      }
    } catch {
      localStorage.removeItem("gradiance_instructor_session");
    }
  };

  useEffect(() => {
    loadCourses();
    loadActiveSession();
    loadTestStudents();
  }, []); // eslint-disable-line

  // While no session is open, quietly re-check so a calendar auto-opened
  // session shows up without a page refresh.
  useEffect(() => {
    if (session) return;
    const id = setInterval(() => {
      loadActiveSession();
    }, 45000);
    return () => clearInterval(id);
  }, [session]); // eslint-disable-line

  const openSession = async (policy) => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/queue/sessions", {
        method: "POST",
        body: JSON.stringify({ policy }),
      });
      const data = await res.json();
      if (res.ok) {
        const sessionObj = {
          session_id: data.session_id,
          instructor_id: data.instructor_id,
          policy: data.policy,
          display_label: data.display_label || null,
        };
        setSession(sessionObj);
        setPolicy(data.policy);
        setPooling(data.pooling || null);
        setEntries([]);
        setIsPaused(false);
        setIsLocked(false);
        setClassPriorityCourseIds([]);
        setBlockNonPriority(false);
        loadTestStudents(); // refresh so the demo panel is current
        localStorage.setItem(
          "gradiance_instructor_session",
          JSON.stringify(sessionObj),
        );
      } else if (res.status === 409) {
        await loadActiveSession();
      } else {
        setError(data.detail || "Could not open session.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeSession = async () => {
    if (!session) return;
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}`, {
        method: "DELETE",
      });
      setSession(null);
      setEntries([]);
      setClassPriorityCourseIds([]);
      setBlockNonPriority(false);
      localStorage.removeItem("gradiance_instructor_session");
    } catch (err) {
      setError(err.message);
    }
  };

  const callStudent = async (entryId) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/call`, {
        method: "PUT",
        body: JSON.stringify({ entry_id: entryId }),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const callNext = async () => {
    if (!session) return;
    try {
      // Card stack refreshes via the same SSE broadcast as a per-card call,
      // since call_next delegates to call_student server-side.
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/call-next`,
        {
          method: "PUT",
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Could not call the next student.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const returnToQueue = async (entryId, spotsBack) => {
    if (!session) return;
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/return-to-queue`,
        {
          method: "PUT",
          body: JSON.stringify({ entry_id: entryId, spots_back: spotsBack }),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Could not send the student back.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const completeStudent = async (entryId) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/complete`, {
        method: "PUT",
        body: JSON.stringify({ entry_id: entryId }),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const reorderEntries = async (newEntryIds) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/reorder`, {
        method: "PUT",
        body: JSON.stringify({ entry_ids: newEntryIds }),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const togglePause = async () => {
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/pause`,
        { method: "PUT" },
      );
      const data = await res.json();
      setIsPaused(data.is_paused);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleLock = async () => {
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/lock`,
        { method: "PUT" },
      );
      const data = await res.json();
      setIsLocked(data.swap_locked);
    } catch (err) {
      setError(err.message);
    }
  };

  const changePolicy = async (newPolicy) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/policy`, {
        method: "PUT",
        body: JSON.stringify({ policy: newPolicy }),
      });
      setPolicy(newPolicy);
    } catch (err) {
      setError(err.message);
    }
  };

  const sendAnnouncement = async (message) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/announce`, {
        method: "PUT",
        body: JSON.stringify({ message }),
      });
      setAnnouncement(message);
    } catch (err) {
      setError(err.message);
    }
  };

  const clearAnnouncement = async () => {
    // With no active session there's nothing on the server to clear — the
    // session that held the announcement is closed. Just dismiss the banner
    // locally so a stale message can always be removed, session or not.
    if (!session?.session_id) {
      setAnnouncement("");
      return;
    }
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/announce`, {
        method: "PUT",
        body: JSON.stringify({ message: "" }),
      });
      setAnnouncement("");
    } catch (err) {
      setError(err.message);
    }
  };

  const setClassPriority = async (courseIds) => {
    try {
      await apiFetch(
        `/api/queue/sessions/${session.session_id}/class-priority`,
        {
          method: "PUT",
          body: JSON.stringify({ course_ids: courseIds }),
        },
      );
      setClassPriorityCourseIds(courseIds);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleBlockNonPriority = async () => {
    if (!session) return;
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/block-non-priority`,
        { method: "PUT" },
      );
      if (res.ok) {
        const data = await res.json();
        setBlockNonPriority(data.block_non_priority);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const removeNonPriority = async () => {
    if (!session) return;
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/remove-non-priority`,
        { method: "POST" },
      );
      if (res.ok) {
        const data = await res.json();
        // Refresh the instructor view so the queue reflects the removals.
        const detail = await apiFetch(
          `/api/queue/sessions/${session.session_id}/instructor`,
        );
        if (detail.ok) {
          const d = await detail.json();
          setEntries(d.entries || []);
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const loadTestStudents = async () => {
    try {
      const res = await apiFetch("/api/courses/test-students");
      if (res.ok) setTestStudents(await res.json());
    } catch {
      /* leave empty */
    }
  };

  const viewAsTestStudent = async (ts) => {
    try {
      const res = await apiFetch(
        `/api/courses/test-students/${ts.id}/view-as`,
        {
          method: "POST",
        },
      );
      if (res.ok) {
        const { token } = await res.json();
        // Open the student view in a new tab with the short-lived token.
        // INTEGRATION NOTE: the student page must accept this token. This passes
        // it as ?demo_token=... — adjust the param name / mechanism to match how
        // your app bootstraps auth from a URL (or store it and navigate).
        // Also pass the display name so the demo tab can show "Test Student 2"
        // rather than a generic label (the token itself carries only id + role).
        const nameParam = ts.display_name
          ? `&demo_name=${encodeURIComponent(ts.display_name)}`
          : "";
        const url = `${window.location.origin}/student?demo_token=${encodeURIComponent(token)}${nameParam}`;
        window.open(url, "_blank", "noopener");
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Could not open student view.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const addTestStudent = async (ts) => {
    if (!session) return;
    // Use the student's first enrolled course; join as a general question.
    const courseId = ts.courses && ts.courses.length ? ts.courses[0].id : null;
    if (!courseId) {
      setError(
        "That test student isn't enrolled in any course. Enroll them in the Manage tab first.",
      );
      return;
    }
    try {
      const res = await apiFetch(`/api/courses/test-students/${ts.id}/join`, {
        method: "POST",
        body: JSON.stringify({
          session_id: session.session_id,
          course_id: courseId,
        }),
      });
      if (res.ok) {
        const detail = await apiFetch(
          `/api/queue/sessions/${session.session_id}/instructor`,
        );
        if (detail.ok) {
          const d = await detail.json();
          setEntries(d.entries || []);
        }
      } else {
        const d = await res.json().catch(() => ({}));
        if (d?.detail?.violation) {
          setNedry({
            message: d.detail.message,
            code: d.detail.code,
            imageUrl: d.detail.image_url || null,
            imageAlt: d.detail.image_alt || "",
          });
        } else {
          setError(d.detail || "Could not add test student.");
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const forceShuffle = async () => {
    if (!session) return;
    try {
      const res = await apiFetch(
        `/api/queue/sessions/${session.session_id}/pool-shuffle`,
        {
          method: "PUT",
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "Could not shuffle.");
        return;
      }
      // The shuffle broadcasts over SSE, but refetch immediately so the new
      // order and cleared pooling state show without waiting for the event.
      const view = await apiFetch(
        `/api/queue/sessions/${session.session_id}/instructor`,
      );
      if (view.ok) {
        const data = await view.json();
        setEntries(data.entries || []);
        setPooling(data.pooling || null);
        setClassPriorityCourseIds(data.class_priority_course_ids || []);
        setBlockNonPriority(data.block_non_priority || false);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const messageStudent = async (entryId, studentId, message) => {
    try {
      await apiFetch(
        `/api/queue/sessions/${session.session_id}/message-student`,
        {
          method: "PUT",
          body: JSON.stringify({ student_id: studentId, message }),
        },
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const removeStudent = async (entryId, message) => {
    try {
      await apiFetch(
        `/api/queue/sessions/${session.session_id}/remove-student`,
        {
          method: "PUT",
          body: JSON.stringify({ entry_id: entryId, message: message || null }),
        },
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const clearQueue = async (message) => {
    try {
      await apiFetch(`/api/queue/sessions/${session.session_id}/clear`, {
        method: "PUT",
        body: JSON.stringify({ message: message || null }),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  // Pronouns ride on the instructor-view payload only — never on the SSE
  // broadcast, which is one message delivered to every client on the session.
  // So they'd appear on load and vanish on the next queue update. Remembering
  // them by student_id and merging them back in fixes that without putting
  // anyone's pronouns in front of their classmates. Pronouns effectively never
  // change mid-session, so a map refreshed on load and on every instructor-view
  // fetch is plenty current.
  const [pronounsById, setPronounsById] = useState({});

  useEffect(() => {
    const found = {};
    for (const e of entries) {
      if (e.pronouns) found[e.student_id] = e.pronouns;
    }
    if (Object.keys(found).length) {
      setPronounsById((prev) => ({ ...prev, ...found }));
    }
  }, [entries]);

  const mergedEntries = useMemo(
    () =>
      entries.map((e) =>
        e.pronouns ? e : { ...e, pronouns: pronounsById[e.student_id] },
      ),
    [entries, pronounsById],
  );

  const waitingEntries = mergedEntries.filter((e) => e.status === "WAITING");
  const calledEntry = entries.find((e) => e.status === "CALLED");

  return (
    <div className={styles.layout}>
      {activeTab === "queue" && (
        <SidePanel
          open={sidePanelOpen}
          session={session}
          policy={policy}
          isPaused={isPaused}
          isLocked={isLocked}
          courses={courses}
          classPriorityCourseIds={classPriorityCourseIds}
          onOpenSession={openSession}
          onCloseSession={closeSession}
          onTogglePause={togglePause}
          onToggleLock={toggleLock}
          onChangePolicy={changePolicy}
          onAnnounce={sendAnnouncement}
          onSetClassPriority={setClassPriority}
          onForceShuffle={forceShuffle}
          blockNonPriority={blockNonPriority}
          onToggleBlockNonPriority={toggleBlockNonPriority}
          onRemoveNonPriority={removeNonPriority}
          testStudents={testStudents}
          onAddTestStudent={addTestStudent}
          onViewAsTestStudent={viewAsTestStudent}
          entries={mergedEntries}
          onCallNext={callNext}
          pooling={pooling}
          loading={loading}
        />
      )}

      <div className={styles.main}>
        <TopBar
          session={session}
          waitingCount={waitingEntries.length}
          isPaused={isPaused}
          isLocked={isLocked}
          policy={policy}
          onToggleSidePanel={() => setSidePanelOpen((o) => !o)}
          sidePanelOpen={sidePanelOpen}
        />

        <div className={styles.tabBar}>
          <button
            className={`${styles.tab} ${activeTab === "queue" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("queue")}
          >
            Queue
            {session && waitingEntries.length > 0 && (
              <span className={styles.tabBadge}>{waitingEntries.length}</span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === "manage" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("manage")}
          >
            Manage
          </button>
          <button
            className={`${styles.tab} ${activeTab === "manage_semester" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("manage_semester")}
          >
            Manage Semester
          </button>
          <button
            className={`${styles.tab} ${activeTab === "predictions" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("predictions")}
          >
            Predictions
          </button>
          <button
            className={`${styles.tab} ${activeTab === "analytics" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            Analytics
          </button>
          <button
            className={styles.tab}
            onClick={chime.toggle}
            aria-pressed={chime.enabled}
            style={{ marginLeft: "auto" }}
            title={
              chime.enabled
                ? "Sound on — chimes when a student joins the queue. Click to turn off."
                : "Sound off — click to be alerted when a student joins."
            }
          >
            <span aria-hidden="true">{chime.enabled ? "🔔" : "🔕"}</span>
            <span className="sr-only">
              {chime.enabled
                ? "Turn off the sound alert for new students joining"
                : "Turn on a sound alert for new students joining"}
            </span>
          </button>
          {user?.role === "ADMIN" && (
            <button
              className={`${styles.tab} ${activeTab === "admin" ? styles.tabActive : ""}`}
              onClick={() => setActiveTab("admin")}
            >
              Admin
            </button>
          )}
        </div>

        {activeTab === "queue" && (
          <>
            {announcement && (
              <AnnouncementBanner
                message={announcement}
                onDismiss={clearAnnouncement}
              />
            )}

            {error && (
              <div className={styles.errorBar}>
                {error}
                <button
                  onClick={() => setError("")}
                  className={styles.errorClose}
                >
                  ✕
                </button>
              </div>
            )}

            {calledEntry && (
              <div className={`${styles.calledBanner} pulse-gold`}>
                <span className={styles.calledIcon}>📢</span>
                <span>
                  <strong>{calledEntry.display_name}</strong> has been called
                </span>
                <button
                  className="btn-primary"
                  onClick={() => completeStudent(calledEntry.entry_id)}
                >
                  Mark Complete
                </button>
                <button
                  className="btn-secondary"
                  onClick={() =>
                    setReturnPrompt({
                      entryId: calledEntry.entry_id,
                      name: calledEntry.display_name,
                      spots: 3,
                    })
                  }
                  title="They were called but aren't here — put them back in the queue"
                >
                  Send Back
                </button>
              </div>
            )}

            <div className={styles.queueArea}>
              {!session ? (
                <div className={styles.empty}>
                  <div className={styles.emptyIcon}>⬡</div>
                  <p>No active queue session.</p>
                  <p className={styles.emptyHint}>
                    Open a session from the side panel to get started.
                  </p>
                </div>
              ) : waitingEntries.length === 0 ? (
                <div className={styles.empty}>
                  <div className={styles.emptyIcon}>✓</div>
                  <p>Queue is empty.</p>
                  <p className={styles.emptyHint}>
                    Waiting for students to join.
                  </p>
                </div>
              ) : (
                <QueueCardStack
                  entries={waitingEntries}
                  isLocked={isLocked}
                  isPaused={isPaused}
                  onCall={callStudent}
                  onReorder={reorderEntries}
                  onMessageStudent={messageStudent}
                  onRemove={removeStudent}
                  onClearQueue={clearQueue}
                  onlineStudentIds={onlineStudentIds}
                />
              )}
            </div>
          </>
        )}

        {activeTab === "manage" && <ManageTab />}
        {activeTab === "manage_semester" && <ManageSemesterTab />}
        {activeTab === "predictions" && <PredictionsTab session={session} />}
        {activeTab === "analytics" && <AnalyticsTab />}
        {activeTab === "admin" && <AdminTab />}
      </div>

      {returnPrompt && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: 24,
              width: 380,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <span
              style={{ fontWeight: 600, color: "var(--txt)", fontSize: 14 }}
            >
              Send {returnPrompt.name} back?
            </span>
            <span
              style={{ fontSize: 12, color: "var(--txt-dim)", lineHeight: 1.5 }}
            >
              They keep their original wait time but move down this many spots.
              Leave as-is to use your default.
            </span>
            <div>
              <label
                style={{
                  fontSize: 11,
                  color: "var(--txt-faint)",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Spots back
              </label>
              <input
                type="number"
                min={0}
                max={50}
                autoFocus
                value={returnPrompt.spots}
                onChange={(e) =>
                  setReturnPrompt((p) => ({ ...p, spots: e.target.value }))
                }
                style={{ width: 90, fontSize: 13, padding: "6px 9px" }}
              />
            </div>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                className="btn-ghost"
                onClick={() => setReturnPrompt(null)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  returnToQueue(
                    returnPrompt.entryId,
                    Number(returnPrompt.spots) || 0,
                  );
                  setReturnPrompt(null);
                }}
              >
                Send Back
              </button>
            </div>
          </div>
        </div>
      )}

      {nedry && (
        <NedryModal
          message={nedry.message}
          code={nedry.code}
          imageUrl={nedry.imageUrl}
          imageAlt={nedry.imageAlt}
          onClose={() => setNedry(null)}
        />
      )}
    </div>
  );
}
